import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { aiQuestionService, AskQuestionRequest, AskQuestionResponse, RatingValue, GrammarPoint, Chunk } from '../../services/aiQuestionService';
import { creatorQuestionsService, SentenceFaq } from '../../services/creatorQuestionsService';
import { useTokens } from '../../contexts/TokenContext';
import { TOKEN_COSTS } from '../../types/tokens';
import './AskQuestion.css';

/**
 * Represents a single message in the conversation
 */
interface Message {
  id: string;
  logId?: string;  // Backend log ID for rating
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
  model?: string;
  complexity?: string;
  cost?: {
    inputTokens: number;
    outputTokens: number;
    estimatedCost: number;
  };
  responseTime?: number;
  rating?: RatingValue | null;  // User's rating: 1, -1, or null
}

/**
 * Props for the AskQuestion component
 * All context is passed from parent; backend decides what to use based on question type
 */
interface AskQuestionProps {
  courseId?: string;
  segmentId?: string;
  currentSentence?: string;
  translation?: string;
  vocabulary?: string[];
  grammarPoints?: GrammarPoint[];
  chunks?: Chunk[];
  autoOpen?: boolean;
}

/**
 * AskQuestion Component
 * A floating Q&A panel for AI-powered learning assistance
 */
const AskQuestion: React.FC<AskQuestionProps> = ({
  courseId,
  segmentId,
  currentSentence,
  translation,
  vocabulary,
  grammarPoints,
  chunks,
  autoOpen = false,
}) => {
  const { t } = useTranslation('learning');
  const { stats, refreshStats } = useTokens();
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnread, setHasUnread] = useState(false);
  const [tokenInfo, setTokenInfo] = useState<{
    freeRemaining: number;
    balance: number;
  } | null>(null);

  // Per-sentence FAQs (creator-curated answers shown when panel opens)
  const [faqs, setFaqs] = useState<SentenceFaq[]>([]);

  // Escalation modal state — opens after a learner thumbs-down an AI answer
  const [escalateTarget, setEscalateTarget] = useState<{ logId: string; question: string } | null>(null);
  const [escalateText, setEscalateText] = useState('');
  const [escalateShowName, setEscalateShowName] = useState(false);
  const [escalateSubmitting, setEscalateSubmitting] = useState(false);
  const [escalateSuccess, setEscalateSuccess] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Clear unread indicator when panel opens
  useEffect(() => {
    if (isOpen) {
      setHasUnread(false);
    }
  }, [isOpen]);

  // Open the panel when an in-app navigation flips autoOpen on (e.g. clicking
  // a creator-answered notification while already on a learning page).
  useEffect(() => {
    if (autoOpen) setIsOpen(true);
  }, [autoOpen]);

  // Fetch creator-curated FAQs for the current sentence whenever the panel
  // opens or the learner moves to a new segment. Cheap query (no AI cost) —
  // shown above the AI conversation as "the creator answered this before"
  // cards. Silently no-ops if courseId / segmentId are not yet known.
  useEffect(() => {
    if (!isOpen || !courseId || !segmentId) {
      setFaqs([]);
      return;
    }
    let cancelled = false;
    creatorQuestionsService
      .getSentenceFaqs(courseId, segmentId)
      .then((items) => {
        if (!cancelled) setFaqs(items);
      })
      .catch(() => {
        if (!cancelled) setFaqs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, courseId, segmentId]);

  // Initialize token info from stats. AI Q&A is Tier 2 (@AdGated):
  // free users watch one rewarded ad → 3 turns/day, after which they fall
  // back to token spend. So this counter is the *ad-unlocked* daily quota,
  // NOT a "free forever" allowance — UI copy should not imply "free".
  useEffect(() => {
    if (stats) {
      setTokenInfo(prev => {
        if (!prev) {
          // First initialization — actual remaining is filled in from the
          // first API response.
          return {
            freeRemaining: 3,
            balance: stats.balance,
          };
        }
        // Only update if balance actually changed
        if (prev.balance !== stats.balance) {
          return { ...prev, balance: stats.balance };
        }
        return prev; // Return same reference to avoid re-render
      });
    }
  }, [stats]);

  /**
   * Generate a unique ID for messages
   */
  const generateId = (): string => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  /**
   * Handle sending a question to the AI
   */
  const handleSend = async () => {
    const question = inputValue.trim();
    if (!question || isLoading) return;

    // Add user message
    const userMessage: Message = {
      id: generateId(),
      type: 'user',
      content: question,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setError(null);
    setIsLoading(true);

    try {
      // Send all available context; backend decides what to use based on question type
      const request: AskQuestionRequest = {
        question,
        context: {
          courseId,
          sentence: currentSentence,
          translation,
          vocabulary,
          grammarPoints,
          chunks,
        },
      };

      const response: AskQuestionResponse = await aiQuestionService.ask(request);

      // Add AI response message
      const aiMessage: Message = {
        id: generateId(),
        logId: response.id,  // Store backend log ID for rating
        type: 'ai',
        content: response.answer,
        timestamp: new Date(),
        model: response.model,
        complexity: response.complexity,
        cost: response.cost,
        responseTime: response.responseTimeMs,
        rating: null,  // No rating yet
      };

      setMessages((prev) => [...prev, aiMessage]);

      // Update token info from response
      if (response.tokenUsage) {
        setTokenInfo({
          freeRemaining: response.tokenUsage.freeRemaining,
          balance: response.tokenUsage.newBalance,
        });
        // Also refresh global token stats
        refreshStats();
      }

      // Set unread indicator if panel is closed
      if (!isOpen) {
        setHasUnread(true);
      }
    } catch (err: any) {
      console.error('Failed to get AI response:', err);

      // Handle insufficient tokens error
      if (err.response?.data?.code === 'INSUFFICIENT_TOKENS') {
        const data = err.response.data;
        setError(t('aiQA.insufficientTokens', {
          required: data.required,
          current: data.current
        }));
        // Update token info to show current balance
        if (data.current !== undefined) {
          setTokenInfo(prev => prev ? { ...prev, balance: data.current, freeRemaining: 0 } : null);
        }
      } else {
        setError(err.response?.data?.message || err.message || t('aiQA.error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle the panel open/closed
   */
  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  /**
   * Handle rating an AI answer
   */
  const handleRate = async (messageId: string, logId: string, rating: RatingValue) => {
    try {
      await aiQuestionService.rate(logId, rating);

      // Update the message with the rating
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, rating } : msg
        )
      );
    } catch (err) {
      console.error('Failed to rate answer:', err);
    }
  };

  /**
   * Open the "ask the creator" modal seeded with the original question that
   * triggered the 👎'd AI answer. Only callable when segmentId is known.
   */
  const handleOpenEscalate = (logId: string) => {
    if (!segmentId || !courseId) return;
    // Find the user's question that immediately preceded this AI message
    const aiIdx = messages.findIndex((m) => m.logId === logId);
    const userMsg = aiIdx > 0 ? [...messages].slice(0, aiIdx).reverse().find((m) => m.type === 'user') : null;
    setEscalateTarget({ logId, question: userMsg?.content ?? '' });
    setEscalateText(userMsg?.content ?? '');
    setEscalateShowName(false);
    setEscalateSuccess(false);
  };

  const handleSubmitEscalate = async () => {
    if (!escalateTarget || !segmentId || !currentSentence || !escalateText.trim()) return;
    setEscalateSubmitting(true);
    try {
      await creatorQuestionsService.escalate({
        aiLogId: escalateTarget.logId,
        segmentId,
        question: escalateText.trim(),
        sentenceText: currentSentence,
        translation,
        showLearnerName: escalateShowName,
      });
      setEscalateSuccess(true);
    } catch (err: any) {
      console.error('Failed to escalate question:', err);
      setError(err.response?.data?.message || err.message || t('aiQA.escalateError'));
    } finally {
      setEscalateSubmitting(false);
    }
  };

  const handleCloseEscalate = () => {
    setEscalateTarget(null);
    setEscalateText('');
    setEscalateShowName(false);
    setEscalateSuccess(false);
  };

  return (
    <div className="ask-question">
      {/* Floating Button */}
      <button
        className={`ask-question__fab ${isOpen ? 'ask-question__fab--active' : ''}`}
        onClick={togglePanel}
        aria-label={isOpen ? t('aiQA.closePanel') : t('aiQA.openPanel')}
      >
        {isOpen ? (
          <span className="ask-question__fab-icon">X</span>
        ) : (
          <span className="ask-question__fab-icon">?</span>
        )}
        {hasUnread && !isOpen && <span className="ask-question__unread-dot" />}
      </button>

      {/* Chat Panel */}
      <div className={`ask-question__panel ${isOpen ? 'ask-question__panel--open' : ''}`}>
        {/* Header */}
        <div className="ask-question__header">
          <div className="ask-question__header-left">
            <h3 className="ask-question__title">{t('aiQA.title')}</h3>
            {tokenInfo && (
              <div className="ask-question__token-info">
                {tokenInfo.freeRemaining > 0 ? (
                  <span className="ask-question__free-badge">
                    {t('aiQA.freeRemaining', { count: tokenInfo.freeRemaining })}
                  </span>
                ) : (
                  <span className="ask-question__token-badge">
                    <span className="ask-question__token-icon">T</span>
                    {tokenInfo.balance}
                    <span className="ask-question__token-cost">
                      ({TOKEN_COSTS.AI_QUESTION}/Q)
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            className="ask-question__close"
            onClick={togglePanel}
            aria-label={t('aiQA.close')}
          >
            X
          </button>
        </div>

        {/* Messages Area */}
        <div className="ask-question__messages">
          {faqs.length > 0 && (
            <div className="ask-question__faqs">
              <div className="ask-question__faqs-title">{t('aiQA.creatorFaqTitle')}</div>
              {faqs.map((faq) => (
                <div key={faq.id} className="ask-question__faq">
                  {faq.questions.length > 0 && (
                    <div className="ask-question__faq-questions">
                      {faq.questions.map((q, idx) => (
                        <div key={idx} className="ask-question__faq-question">
                          <div className="ask-question__faq-question-meta">
                            <span className="ask-question__faq-question-author">
                              {q.learnerDisplayName ?? t('aiQA.faqAnonymousLearner')}
                            </span>
                            <span className="ask-question__faq-question-date">
                              {new Date(q.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="ask-question__faq-question-text">{q.text}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="ask-question__faq-answer">
                    <div className="ask-question__faq-meta">
                      <span className="ask-question__faq-author">
                        {t('aiQA.faqCreatorReply', { name: faq.creatorDisplayName })}
                      </span>
                      <span className="ask-question__faq-date">
                        {new Date(faq.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="ask-question__faq-body">
                      <ReactMarkdown>{faq.answerText}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {messages.length === 0 ? (
            <div className="ask-question__empty">
              <div className="ask-question__empty-icon">?</div>
              <p>{t('aiQA.emptyTitle')}</p>
              <p className="ask-question__empty-hint">
                {t('aiQA.emptyHint')}
              </p>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`ask-question__message ask-question__message--${message.type}`}
                >
                  <div className="ask-question__message-content">
                    {message.type === 'ai' ? (
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                    ) : (
                      <p>{message.content}</p>
                    )}
                  </div>
                  {/* Rating Buttons - only show for AI responses with real IDs (not filtered) */}
                  {message.type === 'ai' && message.logId && !message.logId.startsWith('filtered-') && (
                    <div className="ask-question__rating">
                      <button
                        className={`ask-question__rating-btn ask-question__rating-btn--up ${message.rating === 1 ? 'ask-question__rating-btn--active' : ''}`}
                        onClick={() => handleRate(message.id, message.logId ?? '', 1)}
                        disabled={message.rating !== null}
                        title={t('aiQA.helpful')}
                      >
                        👍
                      </button>
                      <button
                        className={`ask-question__rating-btn ask-question__rating-btn--down ${message.rating === -1 ? 'ask-question__rating-btn--active' : ''}`}
                        onClick={() => handleRate(message.id, message.logId ?? '', -1)}
                        disabled={message.rating !== null}
                        title={t('aiQA.notHelpful')}
                      >
                        👎
                      </button>
                      {message.rating !== null && (
                        <span className="ask-question__rating-thanks">{t('aiQA.thanksFeedback')}</span>
                      )}
                      {/* Escalate to creator: only after 👎, only if we know which sentence the question is about */}
                      {message.rating === -1 && segmentId && courseId && (
                        <button
                          className="ask-question__escalate-btn"
                          onClick={() => handleOpenEscalate(message.logId ?? '')}
                          title={t('aiQA.escalateTooltip')}
                        >
                          {t('aiQA.escalateButton')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </>
          )}

          {/* Loading Indicator */}
          {isLoading && (
            <div className="ask-question__loading">
              <div className="ask-question__loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="ask-question__loading-text">{t('aiQA.loading')}</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="ask-question__error">
              <span className="ask-question__error-icon">!</span>
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="ask-question__input-area">
          <textarea
            ref={inputRef}
            className="ask-question__input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={t('aiQA.placeholder')}
            rows={1}
            disabled={isLoading}
          />
          <button
            className="ask-question__send"
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            aria-label={t('aiQA.send')}
          >
            {t('aiQA.send')}
          </button>
        </div>

        {/* Escalate-to-creator modal */}
        {escalateTarget && (
          <div className="ask-question__escalate-modal" role="dialog" aria-modal="true">
            <div className="ask-question__escalate-card">
              {escalateSuccess ? (
                <>
                  <h4 className="ask-question__escalate-title">{t('aiQA.escalateSuccessTitle')}</h4>
                  <p className="ask-question__escalate-success">{t('aiQA.escalateSuccessBody')}</p>
                  <div className="ask-question__escalate-actions">
                    <button className="ask-question__escalate-primary" onClick={handleCloseEscalate}>
                      {t('aiQA.escalateClose')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h4 className="ask-question__escalate-title">{t('aiQA.escalateTitle')}</h4>
                  <p className="ask-question__escalate-body">{t('aiQA.escalateBody')}</p>
                  {currentSentence && (
                    <div className="ask-question__escalate-sentence">
                      <span className="ask-question__escalate-sentence-label">
                        {t('aiQA.escalateSentenceLabel')}
                      </span>
                      <span className="ask-question__escalate-sentence-text">{currentSentence}</span>
                    </div>
                  )}
                  <textarea
                    className="ask-question__escalate-textarea"
                    value={escalateText}
                    onChange={(e) => setEscalateText(e.target.value)}
                    placeholder={t('aiQA.escalatePlaceholder')}
                    rows={4}
                    disabled={escalateSubmitting}
                  />
                  <label className="ask-question__escalate-checkbox">
                    <input
                      type="checkbox"
                      checked={escalateShowName}
                      onChange={(e) => setEscalateShowName(e.target.checked)}
                      disabled={escalateSubmitting}
                    />
                    <span>{t('aiQA.escalateShowName')}</span>
                  </label>
                  <div className="ask-question__escalate-actions">
                    <button
                      className="ask-question__escalate-secondary"
                      onClick={handleCloseEscalate}
                      disabled={escalateSubmitting}
                    >
                      {t('aiQA.escalateCancel')}
                    </button>
                    <button
                      className="ask-question__escalate-primary"
                      onClick={handleSubmitEscalate}
                      disabled={escalateSubmitting || !escalateText.trim()}
                    >
                      {escalateSubmitting ? t('aiQA.escalateSubmitting') : t('aiQA.escalateSubmit')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AskQuestion;
